"use strict";
var _Result_executors, _Result_promise, _Result_signal;
Object.defineProperty(exports, "__esModule", { value: true });
exports.Result = exports.ResultKind = void 0;
const tslib_1 = require("tslib");
const utils_1 = require("./utils");
function buildErrorMessages(expects, got, error, message, signal) {
    let msg = `${expects.map(k => ResultKind[k]).join(" or ")} but got ${ResultKind[got]}`;
    if (error != null) {
        msg += `; ${error}`;
    }
    if (message != null) {
        msg += `; ${message}`;
    }
    if (signal != null) {
        msg = `[state-flow] Signal '${signal}' expected ${msg}`;
    }
    else {
        msg = `[state-flow] Expected ${msg}`;
    }
    return msg;
}
var ResultKind;
(function (ResultKind) {
    /**
     * At least one handler handled the signal and no errors were thrown.
     */
    ResultKind[ResultKind["OK"] = 0] = "OK";
    /**
     * All handlers and groups ignored the signal or didn't exist.
     */
    ResultKind[ResultKind["Ignored"] = 1] = "Ignored";
    /**
     * At least one handler is in transition and no errors were thrown yet.
     */
    ResultKind[ResultKind["InTransition"] = 2] = "InTransition";
    /**
     * At least one handler rejected the signal.
     */
    ResultKind[ResultKind["Rejected"] = 3] = "Rejected";
    /**
     * At least one handler threw an error.
     */
    ResultKind[ResultKind["Error"] = 4] = "Error";
})(ResultKind || (exports.ResultKind = ResultKind = {}));
/**
 * Represents the result of signal handling or state transition.
 * Multiple results always merge into one with the following rules:
 *
 *  - if at least one result is `Error`, then the final result is `Error`
 *  - if at least one result is `Rejected`, then the final result is `Rejected`
 *  - if a result is `InTransition`, then the final result will be gathered
 *    when transition is finished
 *  - if all results are `Ignored`, then the final result is `Ignored`
 *  - otherwise, the final result is `Handled`
 */
class Result {
    constructor(kind, data) {
        this.kind = kind;
        this.data = data;
        _Result_executors.set(this, []);
        _Result_promise.set(this, null);
        _Result_signal.set(this, null);
        if (kind === ResultKind.InTransition) {
            tslib_1.__classPrivateFieldGet(this, _Result_executors, "f").push(data);
        }
        // immutable object
        Object.freeze(this);
    }
    get ok() {
        return this.kind === ResultKind.OK;
    }
    get hasError() {
        return this.kind === ResultKind.Error || this.kind === ResultKind.Rejected;
    }
    get ready() {
        return this.readyExecutors.then((result) => {
            return new Promise((resolve) => setTimeout(() => resolve(result), 0));
        });
    }
    get error() {
        if (this.data instanceof Error) {
            return this.data;
        }
        return null;
    }
    get message() {
        if (typeof this.data === "string") {
            return this.data;
        }
        return null;
    }
    get readyExecutors() {
        // cache the promise if we haven't already
        // that allows to not use the promise if we don't need to
        // (probably most of the time)
        if (tslib_1.__classPrivateFieldGet(this, _Result_promise, "f") == null) {
            if (this.kind === ResultKind.InTransition) {
                // so for in transition, we need to wait for all the executors to finish
                // and then merge the results
                tslib_1.__classPrivateFieldSet(this, _Result_promise, Promise.all(tslib_1.__classPrivateFieldGet(this, _Result_executors, "f")).then((results) => {
                    return results.reduce((result, next) => result.merge(next), Result.ignore());
                }), "f");
            }
            else {
                // but for everything else, we can just resolve immediately
                tslib_1.__classPrivateFieldSet(this, _Result_promise, Promise.resolve(this), "f");
            }
        }
        return tslib_1.__classPrivateFieldGet(this, _Result_promise, "f");
    }
    static ignore(message) {
        return new Result(ResultKind.Ignored, message);
    }
    static ok(data) {
        return new Result(ResultKind.OK, data);
    }
    static transition(executor, timeout = 15000) {
        return new Result(ResultKind.InTransition, (0, utils_1.withTimeout)(executor.catch((err) => Result.error(err)), timeout));
    }
    static reject(message) {
        return new Result(ResultKind.Rejected, message);
    }
    static error(error) {
        if (error instanceof Error) {
            return new Result(ResultKind.Error, error);
        }
        return new Result(ResultKind.Error, new Error(String(error)));
    }
    expect(...kinds) {
        if (this.kind === ResultKind.InTransition && !kinds.includes(ResultKind.InTransition)) {
            throw new Error("Result is in transition state but expected synchronous result");
        }
        if (!kinds.includes(this.kind)) {
            throw new Error(buildErrorMessages(kinds, this.kind, this.error, this.message, String(tslib_1.__classPrivateFieldGet(this, _Result_signal, "f"))));
        }
        return this;
    }
    withContext(signal) {
        tslib_1.__classPrivateFieldSet(this, _Result_signal, signal, "f");
        return this;
    }
    //
    // //
    // // expectOk(): Result {
    // //   return this.expect(ResultKind.OK);
    // // }
    //
    // async expectAsync(...kinds: ResultKind[]): Promise<Result> {
    //   if (this.kind === ResultKind.InTransition) {
    //     const other = await this.ready;
    //     return other.expect(...kinds);
    //   }
    //   return Promise.resolve(this.expect(...kinds));
    // }
    //
    // async expectAsyncOk(): Promise<Result> {
    //   return this.expectAsync(ResultKind.OK);
    // }
    //
    // mustSyncHandled(acceptIgnored = false): Result {
    //   if (this.kind === ResultKind.InTransition) {
    //     throw new Error("InTransition: Cannot use inTransition result synchronously. Use mustAsyncHandled() instead");
    //   }
    //
    //   if (this.kind !== ResultKind.OK && (!acceptIgnored || this.kind !== ResultKind.Ignored)) {
    //     throw new Error(String(this));
    //   }
    //   return this;
    // }
    //
    // async mustAsyncHandled(acceptIgnored = false): Promise<Result> {
    //   if (this.kind === ResultKind.InTransition) {
    //     const other = await this.ready;
    //     return other.mustSyncHandled(acceptIgnored);
    //   }
    //   return Promise.resolve(this.mustSyncHandled(acceptIgnored));
    // }
    merge(other) {
        var _a, _b;
        // when both results are the same...
        if (this.kind === other.kind) {
            switch (this.kind) {
                case ResultKind.Ignored:
                    // merge the messages
                    return Result.ignore((0, utils_1.mergeStrings)(this.data, other.data)).withContext(tslib_1.__classPrivateFieldGet(this, _Result_signal, "f"));
                case ResultKind.OK:
                    // merge the messages too
                    if (this.data != null || other.data != null) {
                        throw new Error("Cannot merge handled state results with data");
                    }
                    // it doesn't matter which state we return here
                    // because they are the same
                    return this;
                case ResultKind.InTransition:
                    // combine the executors
                    tslib_1.__classPrivateFieldGet(this, _Result_executors, "f").push(...tslib_1.__classPrivateFieldGet(other, _Result_executors, "f"));
                    return this;
                case ResultKind.Rejected:
                    // don't care about other rejected results
                    // matters only the fact that this result is rejected
                    return this;
                case ResultKind.Error:
                    // return MergedError which contains both errors
                    return Result.error(new utils_1.MergedError([this.data, other.data])).withContext(tslib_1.__classPrivateFieldGet(this, _Result_signal, "f"));
            }
        }
        const choose = (a, b) => {
            // we need to decide which result is more important here
            switch (a.kind) {
                case ResultKind.Ignored:
                    // no need to merge here, because Ignored is the least important result
                    return b;
                case ResultKind.InTransition:
                    // just add the other result to the executors
                    // so that it will be merged later
                    tslib_1.__classPrivateFieldGet(a, _Result_executors, "f").push(b.ready);
                    return a;
                case ResultKind.Rejected:
                case ResultKind.Error:
                    // if this result is an error or rejected, then it is the most important
                    // we don't care about the other results
                    return a;
            }
            return null;
        };
        return ((_b = (_a = choose(this, other)) !== null && _a !== void 0 ? _a : choose(other, this)) !== null && _b !== void 0 ? _b : this).withContext(tslib_1.__classPrivateFieldGet(this, _Result_signal, "f"));
    }
    [(_Result_executors = new WeakMap(), _Result_promise = new WeakMap(), _Result_signal = new WeakMap(), Symbol.toStringTag)]() {
        return `SignalResult(${ResultKind[this.kind]})`;
    }
    [Symbol.toPrimitive]() {
        if (this.kind === ResultKind.Error) {
            return `${this.error}${this.message != null ? `; ${this.message}` : ""}`;
        }
        return `${ResultKind[this.kind]}${this.message != null ? `: ${this.message}` : ""}`;
    }
}
exports.Result = Result;
//# sourceMappingURL=result.js.map